import type { RouteRecordRaw } from "vue-router";
import { appStoreBeforeEnter } from "./appStoreRouteGuard";

export const routes = <RouteRecordRaw[]>[
  // Root-level redirect supporting the standalone integrations app (integrations.verbleif.*)
  // handoff: it opens `${webUrl}/app-store/{clientId}/{subscriptionId}`, which resolves here
  // and forwards to the real nested `app-store` route under /settings.
  {
    path: "/app-store/:clientId?/:appSubscriptionId?/:tab?",
    redirect: to => ({ name: "app-store", params: to.params, query: to.query }),
  },
  {
    path: "/settings",

    name: "settings",
    component: () => import("@web/features/Settings/SettingsContainer.vue"),
    redirect: () => {
      return {
        name: "my-account",
      };
    },
    children: [
      {
        path: "/:catchAll(.*)",
        redirect: { name: "my-account" },
      },
      {
        path: "",
        name: "settings-redirect",
        redirect: { name: "my-account" },
      },
      {
        // `connectionId` opens one connected app inside the Apps tab: /settings/my-account/apps/12.
        path: "my-account/:tab?/:connectionId(\\d+)?",
        name: "my-account",
        component: () => import("./Personal/MyAccount.vue"),
      },
      // Preferences and notifications are tabs of My account now; the old addresses keep working.
      {
        path: "preferences",
        name: "my-preferences",
        redirect: { name: "my-account", params: { tab: "preferences" } },
      },
      {
        path: "notifications",
        name: "notifications",
        redirect: { name: "my-account", params: { tab: "notifications" } },
      },
      {
        path: "financials",
        name: "financials",
        component: () => import("./Subscription/SubscriptionView.vue"),
      },
      {
        path: "user-groups/:dialog?",
        name: "user-groups",
        component: () => import("@verbleif/shared/src/composables/UserGroups/UserGroupsOverview.vue"),
      },
      {
        path: "sorts/:dialog?",
        name: "sorts",
        component: () => import("@verbleif/shared/src/composables/Sorts/SortsOverview.vue"),
      },
      {
        path: "property-routes/:dialog?",
        name: "property-routes",
        component: () => import("@verbleif/shared/src/composables/PropertyRoutes/PropertyRoutesOverview.vue"),
      },
      {
        path: "report-topics/:dialog?",
        name: "report-topics",
        component: () => import("@verbleif/shared/src/composables/ReportTopics/ReportTopicOverview.vue"),
      },
      {
        path: "description-hints/:dialog?",
        name: "description-hints",
        component: () => import("@verbleif/shared/src/composables/DescriptionHints/DescriptionHintOverview.vue"),
      },
      {
        path: "property-groups/:dialog?",
        name: "property-groups",
        component: () => import("@verbleif/shared/src/composables/PropertyGroups/PropertyGroupOverview.vue"),
      },
      {
        path: "labels/:dialog?",
        name: "labels",
        component: () => import("@verbleif/shared/src/composables/Labels/LabelsOverview.vue"),
      },
      {
        path: "properties/:dialog?",
        name: "properties",
        component: () => import("@verbleif/shared/src/composables/Properties/PropertyOverview.vue"),
      },
      {
        path: "reservations/:dialog?",
        name: "reservations",
        component: () => import("@verbleif/shared/src/composables/Reservations/ReservationOverview.vue"),
      },
      {
        path: "locations/:dialog?",
        name: "locations",
        component: () => import("@verbleif/shared/src/components/Features/Locations/LocationsOverview.vue"),
      },
      {
        path: "products/:dialog?",
        name: "products",
        component: () => import("@verbleif/shared/src/composables/Products/ProductOverview.vue"),
      },
      {
        path: "inventory-stock",
        name: "inventory-stock",
        component: () => import("@verbleif/shared/src/composables/Products/ProductInventoryStockOverview.vue"),
      },
      {
        path: "inventory-movements",
        name: "inventory-movements",
        component: () => import("@verbleif/shared/src/composables/Products/PropertyInventoryMovementsOverview.vue"),
      },
      {
        path: "inventory-forecast",
        name: "inventory-forecast",
        component: () => import("@verbleif/shared/src/composables/Products/ProductInventoryForecastOverview.vue"),
      },
      {
        path: "warehouses/:dialog?",
        name: "warehouses",
        component: () => import("@verbleif/shared/src/composables/Warehouses/WarehouseOverview.vue"),
      },
      {
        path: "app-store/:clientId?/:appSubscriptionId?/:tab?",
        name: "app-store",
        beforeEnter: appStoreBeforeEnter,
        component: () => import("@verbleif/shared/src/composables/AppStore/AppStoreOverview.vue"),
      },
      {
        path: "roles/:dialog?",
        name: "roles",
        component: () => import("@verbleif/shared/src/composables/PermissionTemplateGroups/PermissionTemplateGroupsOverview.vue"),
      },
      {
        path: "access-roles/:dialog?",
        name: "access-roles",
        component: () => import("@verbleif/shared/src/composables/Roles/OrganisationRolesOverview.vue"),
      },
      {
        path: "location-access-roles/:dialog?",
        name: "location-access-roles",
        component: () => import("@verbleif/shared/src/composables/Roles/LocationRolesOverview.vue"),
      },
      {
        path: "users/:dialog?",
        name: "users",
        component: () => import("@verbleif/shared/src/composables/Users/UsersOverview.vue"),
      },
      {
        path: "general_ledgers/:dialog?",
        name: "general_ledgers",
        component: () => import("@verbleif/shared/src/composables/GeneralLedgers/GeneralLedgerOverview.vue"),
      },
      {
        path: "notification-defaults",
        name: "notifications-organization",
        component: () => import("./NotificationDefaults/NotificationDefaultsPage.vue"),
      },
      {
        path: "preference-defaults",
        name: "user-preferences-organization",
        component: () => import("./PreferenceDefaults/PreferenceDefaultsPage.vue"),
      },
      {
        path: "view-defaults",
        name: "views-organization",
        component: () => import("./Views/ViewsOrganizationSettings.vue"),
      },
      {
        path: "multilingual/:tab?",
        name: "multilingual",
        component: () => import("@verbleif/shared/src/composables/Multilingual/MultilingualSettingsPage.vue"),
      },
      // Languages and translation context are tabs of Meertaligheid now; the old addresses keep working.
      {
        path: "languages",
        name: "languages",
        redirect: to => ({ name: "multilingual", params: { tab: "languages" }, query: to.query }),
      },
      {
        path: "translation-context",
        name: "translation-context",
        redirect: to => ({ name: "multilingual", params: { tab: "translation-context" }, query: to.query }),
      },
    ],
  },
];

export default routes;
